<template>
  <div class="page-wrapper">
    <div class="case-container">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>正在加载案例数据...</p>
      </div>

      <!-- 错误提示 -->
      <div v-if="error" class="error-message">
        <p>{{ error }}</p>
        <button @click="fetchCases" class="retry-btn">重试</button>
      </div>

      <!-- 1. 首页精选案例展示（轮播图） -->
      <section class="carousel-section" v-if="!detailsVisible && allCases.length > 0">
        <div class="carousel">
          <div
            class="carousel-item"
            v-for="(item, index) in allCases.slice(0, 3)"
            :key="item.id"
            v-show="currentIndex === index"
          >
            <img 
              :src="item.image_type === 'upload' ? `https://abc.atmcorr.com/api/uploads//cases/${item.image_path}` : item.image_url" 
              :alt="item.title" 
            />
            <div class="carousel-overlay">
              <h2>{{ item.title }}</h2>
              <p>{{ item.short_description }}</p>
              <div class="tags">
                <span v-for="(tag, idx) in item.tags ? item.tags.split(',') : []" :key="idx">{{ tag }}</span>
              </div>
              <button @click="showDetails(item)">查看详情</button>
            </div>
          </div>
          <!-- 小圆点切换 -->
          <div class="carousel-controls">
            <span
              v-for="(item, index) in allCases.slice(0, 3)"
              :key="item.id"
              :class="{ active: currentIndex === index }"
              @click="currentIndex = index"
            ></span>
          </div>
        </div>
      </section>

      <!-- 2. 案例卡片列表展示 -->
      <section class="case-list" v-if="!detailsVisible && allCases.length > 0">
        <div class="case-card" v-for="item in allCases" :key="item.id">
          <img 
            :src="item.image_type === 'upload' ? `https://abc.atmcorr.com/api/uploads//cases/${item.image_path}` : item.image_url" 
            :alt="item.title" 
          />
          <div class="case-info">
            <h3>{{ item.title }}</h3>
            <p>{{ item.short_description }}</p>
            <div class="tags" v-if="item.tags">
              <span v-for="(tag, idx) in item.tags.split(',')" :key="idx">{{ tag }}</span>
            </div>
            <button @click="showDetails(item)">查看详情</button>
          </div>
        </div>
      </section>

      <!-- 3. 技术参数与处理流程模块 -->
      <section class="tech-parameters" v-if="!detailsVisible && allCases.length > 0">
        <h2>技术参数</h2>
        <table>
          <thead>
            <tr>
              <th>参数类别</th>
              <th>内容</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>使用模型</td>
              <td>MODTRAN（最新版，支持多种大气情景、视角、气溶胶模式）</td>
            </tr>
            <tr>
              <td>光谱通道支持</td>
              <td>支持20余颗卫星40个以上光学载荷，包括全色和多波段通道</td>
            </tr>
            <tr>
              <td>辐射校正精度</td>
              <td>反射率误差：（绝对0.02，相对10%）</td>
            </tr>
            <tr>
              <td>处理模式</td>
              <td>一站式全自动，支持批处理</td>
            </tr>
            <tr>
              <td>数据输入</td>
              <td>原始卫星影像</td>
            </tr>
            <tr>
              <td>数据输出</td>
              <td>经过几何校正后的地表反射率遥感影像产品</td>
            </tr>
            <tr>
              <td>数据格式支持</td>
              <td>Geotiff格式</td>
            </tr>
          </tbody>
        </table>

        <h2>处理流程</h2>
        <div class="flowchart">
          <!-- 流程图图片，替换成实际的流程图路径 -->
          <img :src="flowchartImage" alt="处理流程图" />
        </div>
      </section>

      <!-- 4. 案例详情页面 -->
      <section class="case-detail" v-if="detailsVisible && selectedCase">
        <div class="detail-header">
          <h2>{{ selectedCase.title }}</h2>
          <div class="tags" v-if="selectedCase.tags">
            <span v-for="(tag, idx) in selectedCase.tags.split(',')" :key="idx">{{ tag }}</span>
          </div>
          <p class="release-date" v-if="selectedCase.release_date">
            发布日期：{{ selectedCase.release_date }}
          </p>
        </div>

        <div class="detail-content">
          <div class="info-block">
            <h4>案例简介</h4>
            <p>{{ selectedCase.short_description }}</p>
          </div>

          <div class="info-block">
            <h4>详细描述</h4>
            <p>{{ selectedCase.detail_description }}</p>
          </div>

          <div class="info-block">
            <h4>应用场景和行业背景</h4>
            <p>{{ selectedCase.background }}</p>
          </div>

          <div class="info-block">
            <h4>技术参数和处理流程</h4>
            <p>{{ selectedCase.tech_flow }}</p>
          </div>

          <div class="media-section">
            <!-- 封面图片 -->
            <div class="cover-image" v-if="selectedCase.image_type || selectedCase.image_url">
              <h4>案例封面</h4>
              <img 
                :src="selectedCase.image_type === 'upload' ? `https://abc.atmcorr.com/api/uploads//cases/${selectedCase.image_path}` : selectedCase.image_url" 
                :alt="selectedCase.title" 
              />
            </div>

            <!-- 处理前后对比图 -->
            <div class="image-grid" v-if="selectedCase.before_image || selectedCase.after_image">
              <div v-if="selectedCase.before_image" class="image-container">
                <h4>处理前</h4>
                <img 
                  :src="selectedCase.before_image_type === 'upload' ? `https://abc.atmcorr.com/api/uploads//cases/${selectedCase.before_image_path}` : selectedCase.before_image_url" 
                  alt="处理前" 
                />
              </div>
              <div v-if="selectedCase.after_image" class="image-container">
                <h4>处理后</h4>
                <img 
                  :src="selectedCase.after_image_type === 'upload' ? `https://abc.atmcorr.com/api/uploads//cases/${selectedCase.after_image_path}` : selectedCase.after_image_url" 
                  alt="处理后" 
                />
              </div>
            </div>

            <!-- 演示视频 -->
            <div class="video-container" v-if="selectedCase.video_type || selectedCase.video_url">
              <h4>演示视频</h4>
              <video 
                v-if="selectedCase.video_type === 'upload'"
                :src="`https://abc.atmcorr.com/api/uploads//cases/${selectedCase.video_path}`"
                controls
              ></video>
              <video 
                v-else-if="selectedCase.video_url"
                :src="selectedCase.video_url"
                controls
              ></video>
            </div>
          </div>

          <div class="customer-feedback" v-if="selectedCase.feedback">
            <h4>客户评价</h4>
            <p>{{ selectedCase.feedback }}</p>
          </div>
        </div>

        <div class="detail-footer">
          <button @click="backToList">返回案例列表</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: "Case",
  data() {
    return {
      // 流程图图片（请替换为实际的流程图图片路径）
      flowchartImage: require("@/assets/案例流程.jpg"),
      currentIndex: 0,
      selectedCase: null,
      detailsVisible: false
    };
  },
  computed: {
    ...mapState('case', ['error']),
    ...mapGetters('case', ['allCases', 'currentCase', 'isLoading'])
  },
  methods: {
    ...mapActions('case', ['fetchCases', 'fetchCaseById']),
    
    showDetails(item) {
      this.selectedCase = item;
      this.detailsVisible = true;
    },
    backToList() {
      this.detailsVisible = false;
      this.selectedCase = null;
    }
  },
  created() {
    // 组件创建时获取案例列表
    this.fetchCases();
    
    // 设置轮播图自动切换
    setInterval(() => {
      if (!this.detailsVisible && this.allCases.length > 0) {
        this.currentIndex = (this.currentIndex + 1) % Math.min(3, this.allCases.length);
      }
    }, 5000);
  }
};
</script>

<style scoped>
/* 外层容器设置宽度为1200px，居中显示 */
.page-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* 基本样式 */
.case-container {
  background-color: var(--bg-dark);
  color: var(--text-primary);
  font-family: 'Orbitron', sans-serif;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

/* 量子粒子效果 */
.case-container::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 50% 50%, rgba(0, 180, 255, 0.1) 0%, transparent 50%),
    linear-gradient(45deg, rgba(0, 247, 255, 0.05) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255, 0, 255, 0.05) 25%, transparent 25%);
  background-size: 100% 100%, 60px 60px, 60px 60px;
  background-position: center center, 0 0, 0 0;
  pointer-events: none;
  z-index: 0;
}

/* 加载状态 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--text-primary);
}

.loading-spinner {
  border: 4px solid rgba(0, 180, 255, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--primary);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 错误提示 */
.error-message {
  background: rgba(255, 0, 0, 0.1);
  color: #ff4444;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  border: 1px solid rgba(255, 0, 0, 0.2);
}

.retry-btn {
  background: var(--primary);
  color: var(--text-primary);
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  margin-top: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.retry-btn:hover {
  background: var(--secondary);
  box-shadow: 0 0 15px var(--primary);
}

/* 轮播图样式 */
.carousel-section {
  position: relative;
  width: 100%;
  margin-bottom: 40px;
}

.carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(0, 180, 255, 0.2);
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
}

.carousel-item {
  position: relative;
}

.carousel-item img {
  width: 100%;
  height: 450px;
  display: block;
  filter: brightness(0.8) contrast(1.2);
}

.carousel-overlay {
  position: absolute;
  bottom: 10px;
  left: 20px;
  background: rgba(10, 10, 15, 0.8);
  backdrop-filter: blur(10px);
  padding: 20px;
  border-radius: 8px;
  color: var(--text-primary);
  max-width: 80%;
  border: 1px solid rgba(0, 180, 255, 0.2);
}

.carousel-overlay h2 {
  margin: 0 0 10px;
  font-size: 24px;
  color: var(--primary);
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.carousel-overlay p {
  margin: 0 0 10px;
  font-size: 16px;
  color: var(--text-secondary);
}

.carousel-overlay .tags span {
  background: rgba(0, 180, 255, 0.2);
  color: var(--text-primary);
  padding: 4px 8px;
  margin-right: 5px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid rgba(0, 180, 255, 0.3);
}

.carousel-overlay button {
  background: var(--quantum-glow);
  border: none;
  color: var(--text-primary);
  padding: 10px 20px;
  margin-top: 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.carousel-overlay button:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 15px var(--primary);
}

.carousel-controls {
  text-align: center;
  margin-top: 10px;
}

.carousel-controls span {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: rgba(0, 180, 255, 0.2);
  border-radius: 50%;
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.3s;
}

.carousel-controls span.active {
  background: var(--primary);
  box-shadow: 0 0 10px var(--primary);
}

/* 案例卡片列表 */
.case-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.case-card {
  background: var(--card-bg);
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s;
}

.case-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0 30px rgba(0, 180, 255, 0.2);
}

.case-card img {
  width: 100%;
  height: auto;
  border-radius: 4px;
  filter: brightness(0.8) contrast(1.2);
}

.case-info {
  margin-top: 15px;
}

.case-info h3 {
  font-size: 20px;
  font-weight: bold;
  color: var(--primary);
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.case-info p {
  font-size: 14px;
  margin: 10px 0;
  color: var(--text-secondary);
}

.case-info .tags {
  display: flex;
  flex-wrap: wrap;
  margin: 10px 0;
}

.case-info .tags span {
  background: rgba(0, 180, 255, 0.2);
  color: var(--text-primary);
  padding: 4px 8px;
  margin-right: 5px;
  margin-bottom: 5px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid rgba(0, 180, 255, 0.3);
}

.case-info button {
  background: var(--quantum-glow);
  border: none;
  color: var(--text-primary);
  padding: 10px 20px;
  margin-top: 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.case-info button:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 15px var(--primary);
}

/* 技术参数与流程模块 */
.tech-parameters {
  background: var(--card-bg);
  padding: 30px;
  border-radius: 8px;
  margin-bottom: 40px;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
}

.tech-parameters h2 {
  font-size: 24px;
  margin-bottom: 20px;
  color: var(--primary);
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.tech-parameters table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.tech-parameters th,
.tech-parameters td {
  border: 1px solid rgba(0, 180, 255, 0.2);
  padding: 12px;
  text-align: left;
  font-size: 14px;
  color: var(--text-secondary);
}

.tech-parameters th {
  background: rgba(0, 180, 255, 0.1);
  color: var(--text-primary);
}

.tech-parameters tr:nth-child(even) {
  background: rgba(0, 180, 255, 0.05);
}

.tech-parameters tr:hover {
  background: rgba(0, 180, 255, 0.1);
}

.flowchart {
  text-align: center;
}

.flowchart img {
  max-width: 100%;
  border: 1px solid rgba(0, 180, 255, 0.2);
  border-radius: 4px;
  filter: brightness(0.8) contrast(1.2);
}

/* 案例详情 */
.case-detail {
  background: var(--card-bg);
  padding: 30px;
  border-radius: 8px;
  margin-bottom: 40px;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
}

.detail-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 180, 255, 0.2);
}

.detail-header h2 {
  margin-bottom: 15px;
}

.detail-header .tags {
  margin-bottom: 15px;
}

.release-date {
  color: var(--text-secondary);
  font-size: 14px;
}

.detail-content {
  margin-bottom: 30px;
}

.media-section {
  margin: 30px 0;
}

.cover-image {
  margin-bottom: 30px;
}

.cover-image img {
  width: 100%;
  max-width: 800px;
  height: auto;
  border-radius: 8px;
  filter: brightness(0.8) contrast(1.2);
  border: 1px solid rgba(0, 180, 255, 0.2);
}

.detail-footer {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 180, 255, 0.2);
  text-align: center;
}

.case-detail button {
  background: var(--quantum-glow);
  border: none;
  color: var(--text-primary);
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s;
}

.case-detail button:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 20px var(--primary);
}
</style>
